import React from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect } from 'react';
import {
  MapPin,
  CurrencyCircleDollar,
  Phone,
  CheckCircle,
  ChartLine,
  Robot,
} from '@phosphor-icons/react';

/**
 * ──────────────────────────────────────────────────────────────────────────────
 * PROFESSIONAL HOME PAGE - MARKETLINK MARKETPLACE
 * ──────────────────────────────────────────────────────────────────────────────
 */

// ─── ANIMATION VARIANTS ────────────────────────────────────────────────────
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

// ─── FEATURE CARD COMPONENT ────────────────────────────────────────────────
const FeatureCard = ({
  icon: Icon,
  title,
  description,
  color = 'primary',
}: {
  icon: React.ComponentType<any>;
  title: string;
  description: string;
  color?: string;
}) => (
  <motion.div {...fadeInUp}>
    <Box
      p={6}
      bg="white"
      borderRadius="12px"
      boxShadow="0 4px 8px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)"
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      _hover={{
        boxShadow: '0 16px 32px rgba(0, 0, 0, 0.12)',
        transform: 'translateY(-6px)',
        borderTopColor: 'primary.400',
      }}
      borderTop="4px solid"
      borderTopColor={color === 'brown' ? '#8b7355' : color === 'gold' ? '#b8860b' : '#1a5e3f'}
      position="relative"
      overflow="hidden"
    >
      {/* Background accent */}
      <Box
        position="absolute"
        top={0}
        right={0}
        width="80px"
        height="80px"
        bg={color === 'brown' ? 'rgba(139, 115, 85, 0.05)' : color === 'gold' ? 'rgba(184, 134, 11, 0.05)' : 'rgba(26, 94, 63, 0.05)'}
        borderRadius="0 0 0 80px"
        pointerEvents="none"
      />
      
      <VStack spacing={3} align="start" position="relative" zIndex={1}>
        <Box
          p={3}
          bg={color === 'brown' ? 'rgba(139, 115, 85, 0.1)' : color === 'gold' ? 'rgba(184, 134, 11, 0.1)' : 'rgba(26, 94, 63, 0.08)'}
          borderRadius="8px"
          width="fit-content"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon 
            size={40} 
            weight="duotone"
            color={color === 'brown' ? '#8b7355' : color === 'gold' ? '#b8860b' : '#1a5e3f'}
          />
        </Box>
        <Heading as="h3" size="md" mb={0} color="neutral.900" fontWeight="700" fontSize="16px">
          {title}
        </Heading>
        <Text color="neutral.600" fontSize="14px" lineHeight="1.6">
          {description}
        </Text>
      </VStack>
    </Box>
  </motion.div>
);

// ─── PROGRESS BAR INDICATOR ────────────────────────────────────────────────
const ProgressBar = ({ current, total, duration }: { current: number; total: number; duration: number }) => (
  <HStack
    position="absolute"
    bottom={6}
    left="50%"
    transform="translateX(-50%)"
    spacing={2}
    zIndex={10}
  >
    {Array.from({ length: total }).map((_, i) => (
      <motion.div
        key={i}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: i === current ? 1 : 0 }}
        transition={{ duration: duration / 1000, ease: 'linear' }}
      >
        <Box
          h="3px"
          w={i === current ? '40px' : '8px'}
          bg={i === current ? 'white' : 'rgba(255, 255, 255, 0.4)'}
          borderRadius="2px"
          transition="all 0.3s ease"
          boxShadow={i === current ? '0 0 12px rgba(255, 255, 255, 0.6)' : 'none'}
        />
      </motion.div>
    ))}
  </HStack>
);

// ─── HERO SECTION ──────────────────────────────────────────────────────────
const HeroSection = () => {
  const { isAuthenticated, user } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Three premium agricultural images
  const slides = [
    {
      id: 1,
      image: 'https://images.unsplash.com/photo-1574943320219-553eb213f72d?w=1600&h=900&fit=crop&q=85',
      label: 'Maize Field',
    },

    {
      id: 2,
      image: 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=1600&h=900&fit=crop&q=85',
      label: 'Tractor Spraying Pesticides'
    },

    {
      id: 3,
      image: 'https://images.unsplash.com/photo-1648197752056-86e65cbfe0cb?w=1600&h=900&fit=crop&q=85',
      label: 'Cattle Farm',
    },
  ];

  const SLIDE_DURATION = 5000; // 5 seconds as requested

  useEffect(() => {
    // Preload all images
    slides.forEach((slide) => {
      const img = new Image();
      img.src = slide.image;
    });

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, SLIDE_DURATION);
    
    return () => clearInterval(interval);
  }, [slides.length]);

  const slide = slides[currentSlide];

  return (
    <Box 
      position="relative"
      overflow="hidden"
      minH={{ base: '600px', md: '700px', lg: '800px' }}
    >
      {/* Animated Background Image with Ken Burns Effect */}
      <motion.div
        key={`bg-${currentSlide}`}
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.8, ease: 'easeInOut' }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          overflow: 'hidden',
        }}
      >
        <img
          src={slide.image}
          alt={slide.label}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            display: 'block',
          }}
        />
      </motion.div>

      {/* Fallback background color */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="linear-gradient(135deg, #2d5016 0%, #1a5e3f 50%, #0f3d28 100%)"
        zIndex={0}
        opacity={0.1}
      />

      {/* Professional Dark Overlay - Linear gradient from dark to transparent */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0) 100%)"
        pointerEvents="none"
        zIndex={2}
      />

      {/* Centered Content Overlay */}
      <Container maxW="container.xl" position="relative" zIndex={3} h="full">
        <motion.div 
          variants={staggerContainer} 
          initial="initial" 
          animate="animate"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'inherit',
          }}
        >
          <VStack
            spacing={8}
            align="center"
            textAlign="center"
            py={{ base: 20, md: 32 }}
          >
            {/* Eyebrow - Premium Glassmorphism Badge */}
            <motion.div {...fadeInUp}>
              <HStack
                spacing={2}
                bg="rgba(255, 255, 255, 0.12)"
                px={5}
                py={2.5}
                borderRadius="12px"
                width="fit-content"
                backdropFilter="blur(8px)"
                border="1px solid rgba(255, 255, 255, 0.25)"
                boxShadow="0 8px 32px rgba(0, 0, 0, 0.2)"
              >
                <Box color="white" fontSize="16px" fontWeight="600">
                  ✓
                </Box>
                <Text
                  fontSize="13px"
                  fontWeight="700"
                  color="white"
                  letterSpacing="0.8px"
                  textTransform="uppercase"
                >
                   ZIMBABWE'S LEADING AGRO PLATFORM!
                </Text>
              </HStack>
            </motion.div>

            {/* Main Heading - Refined Typography */}
            <motion.div {...fadeInUp}>
              <Heading
                as="h1"
                color="white"
                fontWeight="900"
                lineHeight="1.15"
                fontSize={{ base: '38px', md: '52px', lg: '66px' }}
                letterSpacing="-1.5px"
                textShadow="0 6px 20px rgba(0, 0, 0, 0.5)"
              >
                Direct Seller-to-Buyer{' '}
                <Box as="span" color="rgba(255, 255, 255, 0.98)">
                  Connections
                </Box>
              </Heading>
            </motion.div>

            {/* Subtitle */}
            <motion.div {...fadeInUp}>
              <Text
                fontSize={{ base: '16px', md: '18px' }}
                color="rgba(255, 255, 255, 0.95)"
                lineHeight="1.8"
                maxW="650px"
                fontWeight="500"
                textShadow="0 3px 10px rgba(0, 0, 0, 0.4)"
              >
                We connect Zimbabwean farmers and agro dealers with genuine buyers across Zimbabwe. Sell your farm products through us and connect with the buyers in no time!
              </Text>
            </motion.div>

            {/* CTA Buttons with Hover Scale */}
            <motion.div {...fadeInUp}>
              <HStack
                spacing={4}
                flexWrap="wrap"
                justify="center"
              >
                {isAuthenticated ? (
                  <>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                    >
                      <Button
                        as={RouterLink}
                        to="/marketplace"
                        variant="primary"
                        size="lg"
                        px={8}
                        fontWeight="700"
                        boxShadow="0 6px 20px rgba(26, 94, 63, 0.4)"
                        _hover={{
                          boxShadow: '0 8px 28px rgba(26, 94, 63, 0.6)',
                        }}
                      >
                        Browse Marketplace
                      </Button>
                    </motion.div>
                    {user?.role === 'farmer' && (
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                      >
                        <Button
                          as={RouterLink}
                          to="/dashboard/add-product"
                          variant="secondary"
                          size="lg"
                          px={8}
                          fontWeight="700"
                        >
                          List Product
                        </Button>
                      </motion.div>
                    )}
                  </>
                ) : (
                  <>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                    >
                      <Button
                        as={RouterLink}
                        to="/register"
                        variant="primary"
                        size="lg"
                        px={8}
                        fontWeight="700"
                        boxShadow="0 6px 20px rgba(26, 94, 63, 0.4)"
                        _hover={{
                          boxShadow: '0 8px 28px rgba(26, 94, 63, 0.6)',
                        }}
                      >
                        Click to Sell or Buy
                      </Button>
                    </motion.div>
                  </>
                )}
              </HStack>
            </motion.div>


          </VStack>

          {/* Progress Bar - Bottom of Hero */}
          <ProgressBar current={currentSlide} total={slides.length} duration={SLIDE_DURATION} />
        </motion.div>
      </Container>
    </Box>
  );
};
const FeaturesSection = () => {
  const features = [
    {
      icon: MapPin,
      title: 'Location Discovery',
      description: 'Find farmlands and farm products based on location with transparent distance information.',
      color: 'primary',
    },
    {
      icon: CurrencyCircleDollar,
      title: 'Fair Market Prices',
      description: 'Through real-time market data and price transparency, buyers negotiate with sellers fairly.',
      color: 'gold',
    },
    {
      icon: Phone,
      title: 'USSD Accessible',
      description: 'Complete functionality via USSD for offline sellers.',
      color: 'primary',
    },
    {
      icon: CheckCircle,
      title: 'Verified Profiles',
      description: 'All sellers are verified. Build trust through reputation scores and reviews.',
      color: 'brown',
    },
    {
      icon: ChartLine,
      title: 'Analytics Dashboard',
      description: 'Track sales, monitor inventory and analyze market trends in real-time.',
      color: 'primary',
    },
    {
      icon: Robot,
      title: 'AI Chat Support',
      description: 'Get instant assistance with product recommendations and market advice.',
      color: 'gold',
    },
  ];

  return (
    <Box 
      bg="linear-gradient(180deg, #f8fdf6 0%, #ffffff 50%, #f5f9f7 100%)" 
      py={{ base: 16, md: 24 }} 
      borderTop="1px solid" 
      borderTopColor="neutral.200"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        left: '-20%',
        top: '20%',
        width: '400px',
        height: '400px',
        bg: 'radial-gradient(circle, rgba(26, 94, 63, 0.05) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
      }}
    >
      <Container maxW="container.xl" position="relative" zIndex={1}>
        <VStack spacing={16} align="stretch">
          {/* Section Header */}
          <VStack spacing={4} align="center" textAlign="center">
            <Badge 
              colorScheme="green" 
              variant="subtle" 
              px={3} 
              py={1}
              fontSize="12px"
              fontWeight="600"
            >
              ✨ Platform Features
            </Badge>
            <Heading
              as="h2"
              color="neutral.900"
              fontWeight="800"
              fontSize={{ base: '32px', md: '40px' }}
              lineHeight="1.2"
            >
              Everything you need to succeed
            </Heading>
            <Text fontSize="16px" color="neutral.600" maxW="600px" lineHeight="1.7">
              Purpose-built tools designed specifically for Zimbabwe's agricultural sector. From farmers to buyers, everyone wins.
            </Text>
          </VStack>

          {/* Features Grid */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 3 }}
              spacing={8}
              w="100%"
            >
              {features.map((feature) => (
                <FeatureCard
                  key={feature.title}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  color={feature.color}
                />
              ))}
            </SimpleGrid>
          </motion.div>
        </VStack>
      </Container>
    </Box>
  );
};

// ─── CTA SECTION ───────────────────────────────────────────────────────
const CTASection = () => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return null;
  }

  return (
    <Box 
      bg="linear-gradient(135deg, #1a5e3f 0%, #2d7a52 50%, #0f3d28 100%)" 
      py={{ base: 12, md: 16 }}
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        top: '-40%',
        right: '-20%',
        width: '400px',
        height: '400px',
        bg: 'radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
      }}
      _after={{
        content: '""',
        position: 'absolute',
        bottom: '-30%',
        left: '-15%',
        width: '350px',
        height: '350px',
        bg: 'radial-gradient(circle, rgba(139, 115, 85, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
      }}
    >
      <Container maxW="container.md" position="relative" zIndex={1}>
        <motion.div {...fadeInUp}>
          <VStack spacing={8} align="center" textAlign="center" py={4}>
            <VStack spacing={3}>
              <Heading 
                as="h2" 
                color="white" 
                fontWeight="800" 
                fontSize={{ base: '28px', md: '36px' }}
                lineHeight="1.2"
              >
                Ready to transform your agricultural business?
              </Heading>
              <Text 
                fontSize="16px" 
                color="rgba(255, 255, 255, 0.95)" 
                lineHeight="1.8"
              >
                Be part of farmers, agro dealers and buyers who are doing business the smart way with MarketLink and transform your agricultural business.
              </Text>
            </VStack>
            <HStack spacing={4} flexWrap="wrap" justify="center" pt={4}>
              <Button
                as={RouterLink}
                to="/register?type=farmer"
                bg="white"
                color="primary.500"
                fontWeight="700"
                fontSize="15px"
                _hover={{
                  bg: 'primary.50',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
                }}
                size="lg"
                px={8}
                h="48px"
                borderRadius="8px"
                transition="all 0.3s ease"
              >
                Get Started as Farmer
              </Button>
              <Button
                as={RouterLink}
                to="/register?type=buyer"
                variant="outline"
                color="white"
                borderColor="white"
                fontWeight="700"
                fontSize="15px"
                _hover={{
                  bg: 'rgba(255,255,255,0.15)',
                  transform: 'translateY(-2px)',
                }}
                size="lg"
                px={8}
                h="48px"
                borderRadius="8px"
                transition="all 0.3s ease"
              >
                Get Started as Buyer
              </Button>
            </HStack>
          </VStack>
        </motion.div>
      </Container>
    </Box>
  );
};

// ─── MAIN HOME COMPONENT ───────────────────────────────────────────────────
const Home: React.FC = () => {
  return (
    <Box>
      <HeroSection />
      <FeaturesSection />
      <CTASection />
    </Box>
  );
};

export default Home;